import { EventEmitter } from "~/store/event";
import { Remote, wrap } from "comlink";
import type { metricsObjectType, MetricsWorker, MetricsWorkerOptions, MetricsDataFormat } from "./metricsWorker";
import { MetricsQuery } from "~/services/APIServices";


/**
 * MetricsClient is a class that initializes a metrics worker and provides a method to update the metrics
 * 
 * @note This class is used to fetch metrics from the worker
 */
export class MetricsClient { 
  private worker?: Remote<MetricsWorker>;
  private instance?: Worker;

  private event = new EventEmitter<{
    'worker:data': [ objectId : string, workerID : string, payload : MetricsDataFormat]; // [objectID, workerID, payload]
    'worker:error': [ objectId : string, workerID : string, error : Error]; // [taskId, workerID, error]
  }>();
  

  /**
   * Initialize the metrics worker
   * @param type - The type of metrics to fetch
   * @param params - The parameters for the metrics query
   * @param token - The authentication token
   * @param updateCallback - The callback function to update the metrics
   * @param options - The options for the metrics worker
   */
  async initialize(type : metricsObjectType, params: MetricsQuery, token: string, updateCallback: (id: string, metrics: MetricsDataFormat ) => void, options: MetricsWorkerOptions = {}) {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'update') {
        this.event.emit('worker:data', e.data.objectID, e.data.workerID, e.data.metrics);
        updateCallback(e.data.objectID, e.data.metrics);
      } else if (e.data?.type === 'error') {
        console.error('Metrics worker error:', e.data.error);
        this.event.emit('worker:error', e.data.objectID, e.data.workerID, e.data.error);
      }
    };

    // Reuse a running worker: booting one re-parses the whole bundle (incl. moment's
    // locale setup, ~100ms+ of CPU), and initialize() is re-dispatched on every time
    // refresh / selection change — a performance trace showed worker re-boots consuming
    // more CPU than the actual metric work (MCS-3969). updateParams fully re-initializes
    // worker state (new query/workerID, cleared buffer, restarted polling) in place.
    if (this.worker && this.instance) {
      this.instance.onmessage = onMessage;
      // stop + initialize (not updateParams): initialize REPLACES the query outright,
      // whereas updateParams merges — stale keys from a previous caller of this shared
      // client (e.g. queryKeys) would otherwise leak into the new query.
      await this.worker.stop();
      await this.worker.initialize(type, params, token, options);
      return;
    }

    this.instance = new Worker(new URL('./metricsWorker', import.meta.url), {
      type: 'module',
      name: `metrics-worker-${options.name || ''}-${type} (${params.metrics.join(',')})`
    });
    this.worker = wrap<MetricsWorker>(this.instance);
    this.instance.onmessage = onMessage;

    this.instance.onerror = (error) => {
      console.error('Worker error:', error);
    };


    await this.worker.initialize(type, params, token, options);
  }

  async updateParams(type : metricsObjectType, params: Partial<MetricsQuery>, token: string, options: MetricsWorkerOptions = {}) {
    if (!this.worker) {
      console.warn('Worker not initialized before updateParams call');
      return null;
    }
    return this.worker.updateParams(type, params, token, options);
  }

  getWorkerID() {
    return this.worker?.getWorkerID();
  }

  /**
   * Terminate the metrics worker
   */
  async terminate() {
    try {
      if (this.worker) {
        await this.worker.terminate();
        this.worker = undefined;
      }
      if (this.instance) {
        this.instance.terminate();
        this.instance = undefined;
      }
    } catch (error) {
      console.error('Error terminating metrics worker:', error);
    }
  }

  /**
   * Stop the metrics worker
   */
  stop() {
    try {
      this.worker?.stop();
    } catch (error) {
      console.error('Error stopping metrics worker:', error);
    }
  }
  eventEmitter() {
    return this.event;
  }
}
